/**
 * PublicWebsitePage — /sites/:slug
 *
 * The public face of a Social Pulse "Instant Website". The URL
 * `/sites/<slug>` is what an organization can share instead of buying
 * a real domain. Renders one of four templates styled by one of eight
 * colour themes.
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2, Mail, Phone, MapPin, Globe, Send, Video, Image as ImageIcon,
  GraduationCap, Trophy, Menu, X, Utensils, Coffee,
  Cookie, Truck, ShoppingBasket, Clock, QrCode,
} from "lucide-react";
import { http } from "@/lib/api";
import VideoEmbed from "@/components/VideoEmbed";
import { normalizeShortUrl, normalizeAssetUrl, synthesizeQrFromShortUrl, displayShortUrl } from "@/lib/splinkUrl";

// Palette table — kept in sync with backend `THEMES`.
const THEMES = {
  blue:   { primary: "#1e40af", accent: "#0ea5e9", surface: "#eff6ff", ink: "#0f172a" },
  green:  { primary: "#14532d", accent: "#22c55e", surface: "#f0fdf4", ink: "#052e16" },
  red:    { primary: "#991b1b", accent: "#ef4444", surface: "#fef2f2", ink: "#450a0a" },
  orange: { primary: "#B25D38", accent: "#f97316", surface: "#FFF7ED", ink: "#2b1a11" },
  purple: { primary: "#5b21b6", accent: "#a855f7", surface: "#faf5ff", ink: "#2e1065" },
  gold:   { primary: "#78350f", accent: "#f59e0b", surface: "#FFFBEB", ink: "#3b1f04" },
  slate:  { primary: "#1f2937", accent: "#64748b", surface: "#f1f5f9", ink: "#0f172a" },
  cream:  { primary: "#5C6B52", accent: "#B25D38", surface: "#FDFBF7", ink: "#2a2a24" },
};

export default function PublicWebsitePage() {
  const { slug } = useParams();
  const [site, setSite] = useState(null);
  const [err, setErr]   = useState(null);

  useEffect(() => {
    http.get(`/websites/${slug}`).then(r => setSite(r.data)).catch(e => setErr(e?.response?.data?.detail || "Not found"));
  }, [slug]);

  if (err) return (
    <div className="max-w-lg mx-auto px-4 py-16 text-center">
      <h1 className="font-display text-3xl mb-2">Website not found</h1>
      <p className="opacity-70 mb-4">{err}</p>
      <Link to="/" className="wofy-link">← Back to Social Pulse</Link>
    </div>
  );
  if (!site) return <div className="p-12 opacity-60 inline-flex gap-2 items-center"><Loader2 className="w-4 h-4 animate-spin" />Loading…</div>;

  const theme = THEMES[site.theme] || THEMES.orange;

  const Template =
    site.template === "educational" ? TemplateEducational :
    site.template === "sports_club" ? TemplateSportsClub  :
    site.template === "minimal"     ? TemplateMinimal     :
    site.template === "restaurant"  ? TemplateRestaurant  :
    site.template === "cafe"        ? TemplateCafe        :
    site.template === "bakery"      ? TemplateBakery      :
    site.template === "food_truck"  ? TemplateFoodTruck   :
    site.template === "grocery"     ? TemplateGrocery     :
                                       TemplateCorporate;

  return (
    <div style={{ background: theme.surface, color: theme.ink, minHeight: "100vh" }} data-testid="public-website">
      <Template site={site} theme={theme} />
      <SocialPulseFooter site={site} theme={theme} />
    </div>
  );
}

// ==================================================================
// Shared bits
// ==================================================================
function SiteHeader({ site, theme, links }) {
  const [open, setOpen] = useState(false);
  return (
    <header className="border-b" style={{ background: "#fff", borderColor: "rgba(0,0,0,0.08)" }}>
      <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-4">
        <div className="flex items-center gap-2 min-w-0">
          {site.logo_url ? (
            <img src={site.logo_url} alt="" className="w-9 h-9 rounded-full object-cover" />
          ) : (
            <div className="w-9 h-9 rounded-full grid place-items-center text-white font-mono text-sm"
                 style={{ background: theme.primary }}>{(site.name || "?")[0]}</div>
          )}
          <div className="truncate">
            <div className="font-medium truncate" style={{ color: theme.primary }}>{site.name}</div>
            {site.tagline && <div className="text-[10px] uppercase tracking-wider opacity-60 truncate">{site.tagline}</div>}
          </div>
        </div>
        <nav className="ml-auto hidden md:flex items-center gap-4 text-sm">
          {links.map(l => <a key={l.href} href={l.href} className="hover:underline">{l.label}</a>)}
        </nav>
        <button className="md:hidden ml-auto" onClick={() => setOpen(v => !v)} aria-label="Menu">
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>
      {open && (
        <div className="md:hidden px-4 pb-3 flex flex-col gap-2 border-t" style={{ borderColor: "rgba(0,0,0,0.05)" }}>
          {links.map(l => <a key={l.href} href={l.href} className="text-sm py-1 hover:underline">{l.label}</a>)}
        </div>
      )}
    </header>
  );
}

function Hero({ site, theme, cta = "Get in touch" }) {
  return (
    <section className="relative overflow-hidden">
      {site.hero_image_url && (
        <div className="absolute inset-0 opacity-30" style={{
          backgroundImage: `url(${site.hero_image_url})`, backgroundSize: "cover", backgroundPosition: "center",
        }} />
      )}
      <div className="relative max-w-6xl mx-auto px-4 py-16 sm:py-24">
        <h1 className="font-display text-4xl sm:text-6xl leading-tight mb-4" style={{ color: theme.primary }}>{site.name}</h1>
        {site.tagline && <div className="text-lg opacity-80 mb-2">{site.tagline}</div>}
        {site.description && <p className="max-w-2xl opacity-90 whitespace-pre-line leading-relaxed">{site.description}</p>}
        <div className="mt-6 flex flex-wrap gap-2">
          <a href="#contact" className="inline-block px-4 py-2 rounded-full text-white text-sm font-medium"
             style={{ background: theme.primary }}>{cta}</a>
          {site.photo_urls?.length > 0 && (
            <a href="#gallery" className="inline-block px-4 py-2 rounded-full text-sm border"
               style={{ borderColor: theme.primary, color: theme.primary }}>See gallery</a>
          )}
        </div>
      </div>
    </section>
  );
}

function GallerySection({ site }) {
  if (!site.photo_urls?.length) return null;
  return (
    <section id="gallery" className="max-w-6xl mx-auto px-4 py-12" data-testid="gallery-section">
      <h2 className="font-display text-3xl mb-4 inline-flex items-center gap-2"><ImageIcon className="w-6 h-6" />Gallery</h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {site.photo_urls.map((p, i) => <img key={i} src={p} alt="" className="w-full aspect-square object-cover rounded-lg" />)}
      </div>
    </section>
  );
}

function VideosSection({ site }) {
  if (!site.video_links?.length) return null;
  return (
    <section id="videos" className="max-w-6xl mx-auto px-4 py-12" data-testid="videos-section">
      <h2 className="font-display text-3xl mb-4 inline-flex items-center gap-2"><Video className="w-6 h-6" />Videos</h2>
      <div className="grid gap-3 sm:grid-cols-2">
        {site.video_links.map((v, i) => <VideoEmbed key={i} video={v} />)}
      </div>
    </section>
  );
}

function PagesSection({ site }) {
  const pages = (site.pages || []).filter(p => p.kind !== "home" && p.kind !== "contact" && p.kind !== "gallery");
  if (!pages.length) return null;
  return (
    <section id="pages" className="max-w-6xl mx-auto px-4 py-12 grid gap-8 md:grid-cols-2">
      {pages.map(p => (
        <div key={p.id} id={p.slug} className="prose max-w-none" data-testid={`page-${p.slug}`}>
          <MarkdownBlock md={p.body_markdown} />
        </div>
      ))}
    </section>
  );
}

function ContactSection({ site, theme }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.message.trim()) { toast.error("Name and message are required."); return; }
    setBusy(true);
    try {
      await http.post(`/websites/${site.slug}/contact`, form);
      setSent(true);
      toast.success("Message sent!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send.");
    } finally { setBusy(false); }
  };

  return (
    <section id="contact" className="border-t" style={{ background: "#fff", borderColor: "rgba(0,0,0,0.08)" }} data-testid="contact-section">
      <div className="max-w-6xl mx-auto px-4 py-12 grid gap-8 md:grid-cols-2">
        <div>
          <h2 className="font-display text-3xl mb-3">Get in touch</h2>
          <div className="text-sm space-y-2 opacity-90">
            {site.contact?.email   && <div className="inline-flex items-center gap-2"><Mail   className="w-4 h-4" />{site.contact.email}</div>}
            {site.contact?.phone   && <div className="inline-flex items-center gap-2"><Phone  className="w-4 h-4" />{site.contact.phone}</div>}
            {site.contact?.address && <div className="inline-flex items-center gap-2"><MapPin className="w-4 h-4" />{site.contact.address}</div>}
            {site.contact?.website && <div className="inline-flex items-center gap-2"><Globe  className="w-4 h-4" /><a href={site.contact.website} target="_blank" rel="noreferrer" className="underline">{site.contact.website}</a></div>}
          </div>
        </div>
        {sent ? (
          <div className="rounded-lg p-5" style={{ background: theme.surface }} data-testid="contact-sent">
            <div className="font-medium mb-1">Thanks!</div>
            <p className="text-sm opacity-80">We got your message. You'll hear back soon.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="grid gap-2" data-testid="contact-form">
            <input required placeholder="Your name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              className="px-3 py-2 rounded-lg border" style={{ borderColor: "rgba(0,0,0,0.1)" }} data-testid="contact-name" />
            <div className="grid gap-2 sm:grid-cols-2">
              <input type="email" placeholder="Email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                className="px-3 py-2 rounded-lg border" style={{ borderColor: "rgba(0,0,0,0.1)" }} data-testid="contact-email" />
              <input placeholder="Phone (optional)" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                className="px-3 py-2 rounded-lg border" style={{ borderColor: "rgba(0,0,0,0.1)" }} data-testid="contact-phone" />
            </div>
            <textarea required placeholder="Message" rows={4} value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
              className="px-3 py-2 rounded-lg border" style={{ borderColor: "rgba(0,0,0,0.1)" }} data-testid="contact-message" />
            <button disabled={busy} className="inline-flex items-center gap-2 justify-center px-4 py-2 rounded-full text-white text-sm font-medium"
              style={{ background: theme.primary }} data-testid="contact-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}Send message
            </button>
          </form>
        )}
      </div>
      <ShareCard site={site} theme={theme} />
    </section>
  );
}

function ShareCard({ site, theme }) {
  const [copied, setCopied] = useState(false);
  const shortUrl = normalizeShortUrl(site.short_url);
  const qrUrl    = normalizeAssetUrl(
    site.qr_code_url || synthesizeQrFromShortUrl(shortUrl),
    process.env.REACT_APP_BACKEND_URL || "",
  );
  if (!shortUrl) return null;
  const displayUrl = displayShortUrl(shortUrl);
  const copy = async () => {
    try { await navigator.clipboard.writeText(shortUrl); setCopied(true); setTimeout(() => setCopied(false), 1500); }
    catch { /* ignore */ }
  };
  return (
    <section id="share" className="max-w-6xl mx-auto px-4 py-12" data-testid="share-card">
      <div
        className="rounded-2xl p-6 sm:p-8 grid gap-6 sm:grid-cols-[auto_1fr] items-center"
        style={{ background: theme.surface, border: `1px dashed ${theme.primary}55` }}
      >
        {qrUrl && (
          <div className="w-40 h-40 rounded-xl bg-white p-3 shadow-sm mx-auto sm:mx-0" data-testid="share-qr-wrap">
            <img
              src={qrUrl}
              alt={`Scan to open ${site.name}`}
              className="w-full h-full object-contain"
              data-testid="share-qr-img"
            />
          </div>
        )}
        <div className="min-w-0">
          <div className="text-[10px] font-mono uppercase tracking-widest opacity-70 mb-1 inline-flex items-center gap-1">
            <QrCode className="w-3 h-3" /> Share this page
          </div>
          <h2 className="font-display text-2xl sm:text-3xl mb-2" style={{ color: theme.primary }}>
            Scan · Tap · Share
          </h2>
          <p className="text-sm opacity-80 mb-3">
            Add the QR to your menu, sign, or business card. Or copy the short link to send it in a text.
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            <a
              href={shortUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg font-mono text-sm bg-white shadow-sm border hover:shadow-md transition-shadow"
              style={{ borderColor: theme.primary, color: theme.primary }}
              data-testid="share-short-url"
            >
              🔗 {displayUrl}
            </a>
            <button
              type="button"
              onClick={copy}
              className="px-3 py-2 rounded-lg text-sm text-white font-semibold hover:opacity-90 transition-opacity"
              style={{ background: theme.primary }}
              data-testid="share-copy-btn"
            >
              {copied ? "✓ Copied" : "Copy link"}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

function SocialPulseFooter({ site, theme }) {
  const shortUrl = normalizeShortUrl(site.short_url);
  return (
    <footer className="border-t py-4 text-center text-[11px] opacity-70" style={{ borderColor: "rgba(0,0,0,0.05)" }}>
      Powered by <Link to="/" className="underline" style={{ color: theme.primary }}>Social Pulse</Link>
      {" · "}
      <span className="opacity-60">sitename: <code>/{`sites/${site.slug}`}</code></span>
      {shortUrl && (
        <>
          {" · "}
          <a href={shortUrl} className="underline inline-flex items-center gap-1" style={{ color: theme.primary }}
             data-testid="short-url">
            <QrCode className="w-3 h-3" />{displayShortUrl(shortUrl)}
          </a>
        </>
      )}
    </footer>
  );
}

const DAY_ORDER = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function HoursSection({ site, theme, dense = false }) {
  const hours = site.contact?.hours;
  const rows = hours && typeof hours === "object" ? DAY_ORDER.map(d => [d, hours[d]]).filter(([, v]) => v) : [];
  if (!rows.length) return null;
  if (dense) {
    return (
      <div className="rounded-lg p-3" style={{ background: theme.surface }} data-testid="hours-section-dense">
        <div className="text-[10px] font-mono uppercase tracking-wider opacity-70 inline-flex items-center gap-1 mb-1"><Clock className="w-3 h-3" />Hours</div>
        <div className="grid grid-cols-2 gap-x-4 text-xs">
          {rows.map(([d, v]) => (
            <div key={d} className="flex justify-between gap-2">
              <span className="opacity-70">{d}</span><span>{v}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }
  return (
    <section id="hours" className="max-w-6xl mx-auto px-4 py-10" data-testid="hours-section">
      <h2 className="font-display text-3xl mb-4 inline-flex items-center gap-2" style={{ color: theme.primary }}><Clock className="w-6 h-6" />Hours</h2>
      <div className="grid gap-1 sm:grid-cols-2 max-w-xl">
        {rows.map(([d, v]) => (
          <div key={d} className="flex justify-between gap-3 py-1.5 border-b" style={{ borderColor: "rgba(0,0,0,0.08)" }}>
            <span className="font-medium">{d}</span><span className="opacity-80">{v}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function MenuSection({ site, theme, title = "Menu", intro = null }) {
  const items = (site.menu || []).filter(i => i.available !== false);
  if (!items.length) return null;
  // Group by section, preserving insertion order.
  const sections = {};
  items.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  for (const it of items) {
    const s = it.section || "Menu";
    if (!sections[s]) sections[s] = [];
    sections[s].push(it);
  }
  const sectionNames = Object.keys(sections);
  const fmt = (i) => {
    if (i.price == null) return "";
    const cur = i.currency || "USD";
    try { return new Intl.NumberFormat(undefined, { style: "currency", currency: cur }).format(i.price); }
    catch { return `${cur} ${i.price}`; }
  };
  return (
    <section id="menu" className="max-w-6xl mx-auto px-4 py-12" data-testid="menu-section">
      <div className="mb-6">
        <h2 className="font-display text-3xl inline-flex items-center gap-2" style={{ color: theme.primary }}>
          <Utensils className="w-6 h-6" />{title}
        </h2>
        {intro && <p className="opacity-80 mt-1 text-sm max-w-2xl">{intro}</p>}
      </div>
      <div className="space-y-8">
        {sectionNames.map(sec => (
          <div key={sec} data-testid={`menu-section-${sec.replace(/\s+/g, "-").toLowerCase()}`}>
            <div className="text-[11px] font-mono uppercase tracking-widest opacity-70 mb-2 pb-1 border-b" style={{ borderColor: "rgba(0,0,0,0.1)" }}>{sec}</div>
            <div className="grid gap-3 sm:grid-cols-2">
              {sections[sec].map(it => (
                <div key={it.id} className="flex gap-3 py-2" data-testid={`menu-item-${it.id}`}>
                  {it.photo_url && (
                    <img src={it.photo_url} alt="" className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline gap-2">
                      <div className="font-medium truncate flex-1">{it.name}</div>
                      {it.price != null && (
                        <div className="text-sm font-mono whitespace-nowrap" style={{ color: theme.primary }}>{fmt(it)}</div>
                      )}
                    </div>
                    {it.description && <div className="text-xs opacity-80 mt-0.5 line-clamp-2">{it.description}</div>}
                    {it.tags?.length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {it.tags.map((t, i) => (
                          <span key={i} className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full"
                            style={{ background: theme.surface, color: theme.primary }}>{t}</span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/** Very light markdown-ish renderer — good enough for MVP. Splits on
 *  headings, paragraphs and dash-bullets. Escapes HTML. */
function MarkdownBlock({ md = "" }) {
  const blocks = useMemo(() => {
    const lines = (md || "").split("\n");
    const out = []; let buf = []; let listBuf = [];
    const flushPara = () => { if (buf.length) { out.push({ type: "p", text: buf.join(" ") }); buf = []; } };
    const flushList = () => { if (listBuf.length) { out.push({ type: "ul", items: listBuf.slice() }); listBuf = []; } };
    for (const raw of lines) {
      const line = raw.trim();
      if (!line) { flushPara(); flushList(); continue; }
      if (line.startsWith("# "))  { flushPara(); flushList(); out.push({ type: "h1", text: line.slice(2) }); continue; }
      if (line.startsWith("## ")) { flushPara(); flushList(); out.push({ type: "h2", text: line.slice(3) }); continue; }
      if (line.startsWith("- ") || line.startsWith("* ")) { flushPara(); listBuf.push(line.slice(2)); continue; }
      flushList(); buf.push(line);
    }
    flushPara(); flushList();
    return out;
  }, [md]);

  return (
    <div>
      {blocks.map((b, i) => {
        if (b.type === "h1") return <h1 key={i} className="font-display text-3xl mb-3">{b.text}</h1>;
        if (b.type === "h2") return <h2 key={i} className="font-display text-2xl mb-3">{b.text}</h2>;
        if (b.type === "ul") return <ul key={i} className="list-disc pl-5 mb-3 space-y-1">{b.items.map((x, j) => <li key={j}>{x}</li>)}</ul>;
        return <p key={i} className="mb-3 leading-relaxed">{b.text}</p>;
      })}
    </div>
  );
}


// ==================================================================
// Templates
// ==================================================================
function baseLinks(site) {
  const seen = new Set(["#top"]);
  const l = [{ href: "#top", label: "Home" }];
  const push = (href, label) => { if (!seen.has(href)) { seen.add(href); l.push({ href, label }); } };
  const pages = (site.pages || []).filter(p => p.kind !== "home");
  pages.forEach(p => push(`#${p.slug}`, p.title));
  if (site.menu?.length)         push("#menu",    "Menu");
  if (site.contact?.hours && Object.keys(site.contact.hours).length) push("#hours", "Hours");
  if (site.photo_urls?.length)   push("#gallery", "Gallery");
  if (site.video_links?.length)  push("#videos",  "Videos");
  push("#contact", "Contact");
  return l;
}

function TemplateCorporate({ site, theme }) {
  return (
    <>
      <SiteHeader site={site} theme={theme} links={baseLinks(site)} />
      <div id="top" />
      <Hero site={site} theme={theme} cta="Contact us" />
      <PagesSection site={site} />
      <GallerySection site={site} />
      <VideosSection site={site} />
      <ContactSection site={site} theme={theme} />
    </>
  );
}

function TemplateEducational({ site, theme }) {
  return (
    <>
      <SiteHeader site={site} theme={theme} links={baseLinks(site)} />
      <div id="top" />
      <Hero site={site} theme={theme} cta="Apply now" />
      <div className="max-w-6xl mx-auto px-4 py-4 flex flex-wrap gap-2 items-center">
        <span className="inline-flex items-center gap-1 text-xs px-3 py-1 rounded-full" style={{ background: theme.surface, color: theme.primary }}>
          <GraduationCap className="w-3.5 h-3.5" />Educational institution
        </span>
      </div>
      <PagesSection site={site} />
      <GallerySection site={site} />
      <VideosSection site={site} />
      <ContactSection site={site} theme={theme} />
    </>
  );
}

function TemplateSportsClub({ site, theme }) {
  return (
    <>
      <div style={{ background: theme.primary, color: "#fff" }}>
        <SiteHeader site={{...site, logo_url: site.logo_url}} theme={{...theme, primary: "#fff"}} links={baseLinks(site)} />
      </div>
      <div id="top" />
      <section className="relative overflow-hidden text-white" style={{ background: theme.primary }}>
        {site.hero_image_url && (
          <div className="absolute inset-0 opacity-40" style={{ backgroundImage: `url(${site.hero_image_url})`, backgroundSize: "cover", backgroundPosition: "center" }} />
        )}
        <div className="relative max-w-6xl mx-auto px-4 py-20 sm:py-28">
          <div className="inline-flex items-center gap-1 text-xs px-3 py-1 rounded-full mb-3" style={{ background: theme.accent }}>
            <Trophy className="w-3.5 h-3.5" />Sports Club
          </div>
          <h1 className="font-display text-5xl sm:text-6xl leading-tight mb-2">{site.name}</h1>
          {site.tagline && <div className="text-lg opacity-90 mb-2">{site.tagline}</div>}
          {site.description && <p className="max-w-2xl opacity-90 whitespace-pre-line">{site.description}</p>}
        </div>
      </section>
      <PagesSection site={site} />
      <VideosSection site={site} />
      <GallerySection site={site} />
      <ContactSection site={site} theme={theme} />
    </>
  );
}

function TemplateMinimal({ site, theme }) {
  return (
    <>
      <SiteHeader site={site} theme={theme} links={baseLinks(site)} />
      <div id="top" />
      <section className="max-w-3xl mx-auto px-4 py-24 text-center">
        <h1 className="font-display text-5xl sm:text-7xl leading-tight mb-6" style={{ color: theme.primary }}>{site.name}</h1>
        {site.tagline && <div className="text-lg opacity-70 mb-3">{site.tagline}</div>}
        {site.description && <p className="opacity-90 whitespace-pre-line leading-relaxed max-w-xl mx-auto">{site.description}</p>}
        <div className="mt-6"><a href="#contact" className="underline">Say hello →</a></div>
      </section>
      <PagesSection site={site} />
      <GallerySection site={site} />
      <VideosSection site={site} />
      <ContactSection site={site} theme={theme} />
    </>
  );
}

// ---------- Food templates ----------

function FoodHero({ site, theme, chip, ChipIcon, cta = "See menu", cta2 = "Contact us", ctaHref = "#menu", cta2Href = "#contact", darkChip = false }) {
  return (
    <section className="relative overflow-hidden">
      {site.hero_image_url && (
        <div className="absolute inset-0" style={{
          backgroundImage: `linear-gradient(rgba(0,0,0,0.25),rgba(0,0,0,0.55)), url(${site.hero_image_url})`,
          backgroundSize: "cover", backgroundPosition: "center",
        }} />
      )}
      <div className={`relative max-w-6xl mx-auto px-4 py-16 sm:py-28 ${site.hero_image_url ? "text-white" : ""}`}>
        {chip && (
          <div className="inline-flex items-center gap-1 text-[11px] px-3 py-1 rounded-full mb-3 font-medium"
               style={{ background: darkChip ? theme.primary : "#fff", color: darkChip ? "#fff" : theme.primary }}>
            {ChipIcon && <ChipIcon className="w-3.5 h-3.5" />}{chip}
          </div>
        )}
        <h1 className="font-display text-4xl sm:text-6xl leading-tight mb-3">{site.name}</h1>
        {site.tagline && <div className="text-base sm:text-lg opacity-90 mb-3 max-w-2xl">{site.tagline}</div>}
        {site.description && <p className="max-w-2xl opacity-90 whitespace-pre-line leading-relaxed">{site.description}</p>}
        <div className="mt-6 flex flex-wrap gap-2">
          <a href={ctaHref} className="inline-block px-5 py-2.5 rounded-full text-white text-sm font-medium hover:opacity-90 transition-opacity"
             style={{ background: theme.accent }}>{cta}</a>
          <a href={cta2Href} className="inline-block px-5 py-2.5 rounded-full text-sm border-2 font-medium hover:bg-white/10 transition-colors"
             style={{ borderColor: site.hero_image_url ? "#fff" : theme.primary, color: site.hero_image_url ? "#fff" : theme.primary }}>{cta2}</a>
        </div>
      </div>
    </section>
  );
}

function TemplateRestaurant({ site, theme }) {
  return (
    <>
      <SiteHeader site={site} theme={theme} links={baseLinks(site)} />
      <div id="top" />
      <FoodHero site={site} theme={theme} chip="Restaurant" ChipIcon={Utensils} cta="View our menu" cta2="Reserve a table" cta2Href="#contact" />
      <MenuSection site={site} theme={theme} title="Our menu" intro="Freshly prepared. Locally sourced. Served with love." />
      <HoursSection site={site} theme={theme} />
      <PagesSection site={site} />
      <GallerySection site={site} />
      <VideosSection site={site} />
      <ContactSection site={site} theme={theme} />
    </>
  );
}

function TemplateCafe({ site, theme }) {
  return (
    <>
      <SiteHeader site={site} theme={theme} links={baseLinks(site)} />
      <div id="top" />
      <FoodHero site={site} theme={theme} chip="Café" ChipIcon={Coffee} cta="Today's specials" cta2="Find us" />
      <div className="max-w-6xl mx-auto px-4 py-8 grid gap-6 md:grid-cols-3">
        <div className="md:col-span-2">
          <MenuSection site={site} theme={theme} title="On the menu" intro="Small-batch coffee, fresh bakes and quiet corners." />
        </div>
        <div className="space-y-4">
          <HoursSection site={site} theme={theme} dense />
          {site.contact?.address && (
            <div className="rounded-lg p-3" style={{ background: theme.surface }}>
              <div className="text-[10px] font-mono uppercase tracking-wider opacity-70 inline-flex items-center gap-1 mb-1">
                <MapPin className="w-3 h-3" />Find us
              </div>
              <div className="text-sm">{site.contact.address}</div>
            </div>
          )}
        </div>
      </div>
      <PagesSection site={site} />
      <GallerySection site={site} />
      <VideosSection site={site} />
      <ContactSection site={site} theme={theme} />
    </>
  );
}

function TemplateBakery({ site, theme }) {
  const items = site.menu || [];
  const featured = items.filter(i => i.photo_url).slice(0, 6);
  return (
    <>
      <SiteHeader site={site} theme={theme} links={baseLinks(site)} />
      <div id="top" />
      <FoodHero site={site} theme={theme} chip="Bakery" ChipIcon={Cookie} cta="Explore treats" cta2="Order & pickup" />
      {featured.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 py-10" data-testid="featured-bakes">
          <h2 className="font-display text-3xl mb-4" style={{ color: theme.primary }}>Baked fresh today</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {featured.map(it => (
              <div key={it.id} className="rounded-2xl overflow-hidden shadow-sm" style={{ background: "#fff" }}>
                <img src={it.photo_url} alt={it.name} className="w-full aspect-square object-cover" />
                <div className="p-3">
                  <div className="text-sm font-medium">{it.name}</div>
                  {it.price != null && (
                    <div className="text-xs font-mono mt-0.5" style={{ color: theme.primary }}>
                      {it.currency || "USD"} {it.price}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      <MenuSection site={site} theme={theme} title="Full bakery menu" />
      <HoursSection site={site} theme={theme} />
      <PagesSection site={site} />
      <GallerySection site={site} />
      <ContactSection site={site} theme={theme} />
    </>
  );
}

function TemplateFoodTruck({ site, theme }) {
  return (
    <>
      <div style={{ background: theme.primary }}>
        <SiteHeader site={site} theme={{ ...theme, primary: "#fff" }} links={baseLinks(site)} />
      </div>
      <div id="top" />
      <section className="relative overflow-hidden text-white" style={{ background: theme.primary }}>
        {site.hero_image_url && (
          <div className="absolute inset-0 opacity-40" style={{ backgroundImage: `url(${site.hero_image_url})`, backgroundSize: "cover", backgroundPosition: "center" }} />
        )}
        <div className="relative max-w-6xl mx-auto px-4 py-20 sm:py-28">
          <div className="inline-flex items-center gap-1 text-xs px-3 py-1 rounded-full mb-3 font-medium" style={{ background: theme.accent }}>
            <Truck className="w-3.5 h-3.5" />Food Truck
          </div>
          <h1 className="font-display text-5xl sm:text-7xl leading-tight mb-3">{site.name}</h1>
          {site.tagline && <div className="text-lg opacity-90 mb-2">{site.tagline}</div>}
          {site.description && <p className="max-w-2xl opacity-90 whitespace-pre-line">{site.description}</p>}
          <div className="mt-6 flex flex-wrap gap-2">
            <a href="#menu" className="inline-block px-5 py-2.5 rounded-full text-sm font-medium hover:opacity-90" style={{ background: "#fff", color: theme.primary }}>What we're serving</a>
            <a href="#contact" className="inline-block px-5 py-2.5 rounded-full text-sm border-2 font-medium hover:bg-white/10" style={{ borderColor: "#fff" }}>Track us today</a>
          </div>
        </div>
      </section>
      <MenuSection site={site} theme={theme} title="Today's menu" />
      <HoursSection site={site} theme={theme} />
      <PagesSection site={site} />
      <GallerySection site={site} />
      <VideosSection site={site} />
      <ContactSection site={site} theme={theme} />
    </>
  );
}

function TemplateGrocery({ site, theme }) {
  return (
    <>
      <SiteHeader site={site} theme={theme} links={baseLinks(site)} />
      <div id="top" />
      <FoodHero site={site} theme={theme} chip="Grocery & Market" ChipIcon={ShoppingBasket} cta="Shop the aisles" cta2="Visit us" />
      <MenuSection site={site} theme={theme} title="Aisles & offerings" intro="Everything you need, curated for our neighborhood." />
      <HoursSection site={site} theme={theme} />
      <PagesSection site={site} />
      <GallerySection site={site} />
      <ContactSection site={site} theme={theme} />
    </>
  );
}
